import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { TreeNode } from 'primeng/api/treenode';
import { TreeBuilder } from '../../components/folder-tree/tree-builder';
import { Folder } from '../../models/site-content/folder.model';
import { DataCollectionService } from '../../services/data-collection.service';
import { FolderFormInstanceTreeService } from '../../services/folder-form-instance-tree.service';
import { FolderService } from '../../services/folder.service';
import { FormBuilderService } from '../../services/form-builder.service';
import { FormInstanceService } from '../../services/form-instance.service';
import { CopyFormsAction } from './actions/copy-forms-action';
import { CreateFolderAction } from './actions/create-folder-action';
import { CreateFormAction } from './actions/create-form-action';
import { DeleteFormsAction } from './actions/delete-forms-action';
import { IHomePageAction } from './actions/i-home-page-action';
import { MoveFormsAction } from './actions/move-forms-action';
import { PublishFolderAction } from './actions/publish-folder-action';
import { SubscribeFolderAction } from './actions/subscribe-folder-action';
import { HomePageActionsEnum, MoveOrCopyEnum, PerformActionParamObj } from './extras';

export class HomePageActionDialogInitData {
    public siteId: number;
    public siteName: string;
    public selectedRows: any[]; 
    public action: string;
    public formId: number;
}

@Component({
    selector: 'app-home-page-action',
    templateUrl: './home-page-action.dialog.html',
    styleUrls: ['./home-page-action.dialog.scss'],
    standalone: false
})
export class HomePageActionDialog implements OnInit {
    // Properties.
    private siteId: number;
    private formId: number;
    private action: string;
    private rows: any[];
    private treeData: TreeNode[];
    private selectedTreeNode: TreeNode;
    private moveOrCopy: string;
    private errorMessage: string;
    private entityName: string;
    private actionPerformer: IHomePageAction;
    private siteName: string;
    private showCloseButton: boolean;
    private disableOkButton: boolean = false;

    // Constructor.
    public constructor(
        @Inject(MAT_DIALOG_DATA) private data,
        public dialogRef: MatDialogRef<HomePageActionDialog>,
        private formInstanceService: FormInstanceService,
        private folderFormInstanceTreeService: FolderFormInstanceTreeService,
        private folderService: FolderService,
        private formBuilderService: FormBuilderService,
        private dataCollectionService: DataCollectionService
    ) {
        let initData: HomePageActionDialogInitData = <HomePageActionDialogInitData>data;
        this.action = initData.action;
        this.rows = initData.selectedRows;
        this.siteId = initData.siteId;
        this.formId = initData.formId;
        this.siteName = initData.siteName;

        this.setActionPerformer(data, formInstanceService, folderService);

        if (this.actionPerformer.requiresTree()) {
            this.buildTree();
        }
    }

    // Life cycle method(s).
    public ngOnInit(): void {
    }

    // Helper methods.        
    private setActionPerformer(data: any, formInstanceService: FormInstanceService, folderService: FolderService) {
        if (this.action == HomePageActionsEnum.ACTION_DELETE) {
            this.actionPerformer = new DeleteFormsAction();
        } else if (this.action == HomePageActionsEnum.ACTION_COPY) {
            this.actionPerformer = new CopyFormsAction();
        } else if (this.action == HomePageActionsEnum.ACTION_MOVE) {
            this.actionPerformer = new MoveFormsAction();
        } else if (this.action == HomePageActionsEnum.ACTION_CREATE_FOLDER) {
            this.actionPerformer = new CreateFolderAction();
        } else if (this.action == HomePageActionsEnum.ACTION_CREATE_FORM) {
            this.actionPerformer = new CreateFormAction();
        } else if (this.action == HomePageActionsEnum.ACTION_PUBLISH_FOLDER) {
            this.actionPerformer = new PublishFolderAction();
        } else if (this.action == HomePageActionsEnum.ACTION_SUBSCRIBE_FOLDER) {
            this.actionPerformer = new SubscribeFolderAction();
        } else {
            throw new Error(`${this.action} is an unknown action!`);
        }

        this.actionPerformer.configure(data.selectedRows, this.siteId, this.formId, formInstanceService, folderService);
    }

    private buildTree() {
        let perm = this.actionPerformer.permissionsRequiredToPerform();
        let rootWriteable = this.actionPerformer.requiresWritableRootNode();
        let treeBuilder = new TreeBuilder(this.folderFormInstanceTreeService);
        treeBuilder.build(this.siteId, false, perm).then(tree => {
            let root = [{
                selectable: treeBuilder.RootFolderIsSelectable && rootWriteable,
                type: treeBuilder.rootFolderType(rootWriteable),
                label: this.siteName,
                children: tree,
                data: { id: treeBuilder.RootFolderId }
            } as TreeNode];
            this.treeData = root;
        });
    }

    // Define getter and setter methods called by my HTML code.
    public get Rows(): any[] {
        return this.rows;
    }

    public get FormInstanceService() {
        return this.formInstanceService;
    }

    public get EntityName(): string {
        return this.entityName;
    }

    public set EntityName(value: string) {
        this.entityName = value;
    }

    public get Message(): string {
        return this.actionPerformer.getMessage();
    }

    public get ErrorMessage(): string {
        let msg = this.errorMessage || this.actionPerformer.getErrorMessage();
        return msg;
    }

    public get ShowCloseButton(): boolean {
        return this.showCloseButton;
    }

    public get DisableOkButton(): boolean {
        return this.disableOkButton;
    }

    public get TreeData(): TreeNode[] {
        return this.treeData;
    }

    public get SelectedTreeNode(): TreeNode {
        return this.selectedTreeNode;
    }

    public set SelectedTreeNode(value: TreeNode) {
        console.log(value);
        this.selectedTreeNode = value;
        console.log(this.selectedTreeNode);
    }

    public get MoveOrCopy(): string {
        return this.moveOrCopy;
    }

    public set MoveOrCopy(value: string) {
        this.moveOrCopy = value;
    }

    public get DeleteForms(): boolean {
        return this.action === HomePageActionsEnum.ACTION_DELETE;
    }

    public get MoveForms(): boolean {
        return this.action === HomePageActionsEnum.ACTION_MOVE;
    }

    public get CopyForms(): boolean {
        return this.action === HomePageActionsEnum.ACTION_COPY;
    }

    public get CreateFolder(): boolean {
        return this.action === HomePageActionsEnum.ACTION_CREATE_FOLDER;
    }

    public get CreateForm(): boolean {
        return this.action === HomePageActionsEnum.ACTION_CREATE_FORM
    }

    public get Title(): string {
        if (this.actionPerformer) {
            return this.actionPerformer.getTitle();
        } else {
            return "!!!!!!!! UNKNOWN ACTION !!!!!!!!";
        }
    }

    // Handle control events.    
    public handleOk() {
        this.errorMessage = null;
        if (this.noTreeNodeSelected()) {
            this.errorMessage = "You need to select a destination Folder";
            return;
        }

        if (this.actionPerformer) {
            this.disableOkButton = true;
            let paramObj = new PerformActionParamObj(this.moveOrCopy, this.selectedTreeNode?.data?.id, this.selectedTreeNode?.label, this.entityName);
            this.actionPerformer.performAction(paramObj).then(x => {
                if (this.actionPerformer.hasDoneMessage()) {
                    this.showCloseButton = true;
                    this.buildTree();
                } else {
                    this.dialogRef.close(x);
                }
            });
        } else {
            throw new Error(`${this.action} is an unknown action!`);
        }
    }

    public closeDialog() {
        this.dialogRef.close();
    }

    public treeNodeSelected(eventData: any): void {
        if ((this.actionPerformer != null) && this.actionPerformer.requiresFormTemplate() &&
            (eventData['node'] != null) && (eventData['node']['data'] != null) && (eventData['node']['data']['id'] != null)) {
            let folderId = eventData['node']['data']['id'];
            let folderName: string = eventData['node']['data']['name'];

            // Does the action performer require the current folder.
            if (this.actionPerformer.requiresCurrentFolder) {
                let folder: Folder = new Folder();
                folder.id = folderId;
                folder.name = folderName;

                this.actionPerformer.setCurrentFolder(folder);
            }

            let initData: HomePageActionDialogInitData = <HomePageActionDialogInitData>this.data;
            this.dataCollectionService.getFormForFolder(initData.formId, folderId).then(formTemplate => {
                this.actionPerformer.setFormTemplate(formTemplate);

                // If the action performer can suggest a non-null entity name, use it.
                let suggestedEntityName: string = this.actionPerformer.getSuggestedEntityName();
                if (suggestedEntityName != null)
                    this.entityName = suggestedEntityName;
            });
        }
    }

    private noTreeNodeSelected() {
        return this.actionRequiresTree() && this.selectedTreeNode == null;
    }

    public createRef(): boolean {
        return this.moveOrCopy == MoveOrCopyEnum.CREATE_REF;
    }

    public copy() {
        return this.moveOrCopy == MoveOrCopyEnum.COPY;
    }

    public move() {
        return this.moveOrCopy == MoveOrCopyEnum.MOVE;
    }

    public actionRequiresTree() {
        return this.actionPerformer.requiresTree();
    }
}
